// Use targeted eslint-disable instead of @ts-nocheck
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Necessary for middleware compatibility

import { configureStore, Middleware, MiddlewareAPI, Dispatch, AnyAction } from '@reduxjs/toolkit';
import { reduxLogger } from '@/core/utils/logger';
import { Provider, TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { config } from '@/config/config';

// Import root reducer that combines all feature slices
import { reducers, RootState } from './rootReducer';

// Import middleware from features
import { apiMiddleware } from '@/api/core/middleware';
import { optimisticMiddleware } from '@/api/core/optimisticUpdates';

// Import API middleware
import { baseApi } from '@/api/core/baseApi';

// Define typed middleware for action tracing (development only)
const actionTracingMiddleware: Middleware = (api: MiddlewareAPI) => (next: Dispatch<AnyAction>) => (action: AnyAction) => {
  // Only trace in development and when explicitly enabled
  if (config.features.debugLogging && config.features.reduxTrace) {
    // Check if we want to log this action
    if (
      action.type && (
      action.type.includes('conversations/setCurrentConversation') ||
      action.type.includes('conversations/addMessage') ||
      action.type.includes('teams/') ||
      action.type.includes('contacts/')
      )
    ) {
      reduxLogger.info('Before action:', action.type, action);
      
      // Call the next dispatch method in the middleware chain
      const result = next(action);
      
      // Get the state after the action
      const stateAfter = api.getState();
      
      // If the action is a messages action, log the related state
      if (action.type.includes('conversations/')) {
        reduxLogger.info('Conversations state:', stateAfter.conversations);
      } else if (action.type.includes('teams/')) {
        reduxLogger.info('Teams state:', stateAfter.teams);
      }
      
      return result;
    }
  }
  
  return next(action);
};

// Create the redux store
export const store = configureStore({
  reducer: {
    ...reducers,
    // Add only the base API reducer (all APIs extend from it)
    [baseApi.reducerPath]: baseApi.reducer,
  },
  middleware: (getDefaultMiddleware) => {
    // Configure middleware
    const defaultMiddleware = getDefaultMiddleware({
      // Enable checks in development only
      serializableCheck: config.features.debugLogging ? {
        // Ignore specific action types that contain non-serializable values
        ignoredActions: [
          'persist/PERSIST', 
          'persist/REHYDRATE',
          // Ignore export mutations that return Blob responses
          'api/executeMutation/fulfilled',
          'api/executeMutation/pending',
          'api/executeMutation/rejected'
        ],
        // Ignore specific paths that might contain non-serializable values
        ignoredPaths: ['api.queries', 'api.mutations', 'payload', 'meta.arg'],
      } : false,
      immutableCheck: config.features.debugLogging ? {
        warnAfter: 128,
      } : false,
      actionCreatorCheck: config.features.debugLogging,
    });
    
    // Add our custom middlewares including RTK Query middleware
    let middlewareChain = defaultMiddleware
      .concat(baseApi.middleware)
      // Add combined API middlewares (request management + WebSocket)
      .concat(apiMiddleware)
      .concat(optimisticMiddleware);

    // Only add action tracing in development
    if (config.features.debugLogging) {
      middlewareChain = middlewareChain.concat(actionTracingMiddleware);
    }

    return middlewareChain;
  }
});

// Re-export RootState from rootReducer
export type { RootState };
// Infer the AppDispatch type
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Export the provider (for easier imports)
export { Provider };

// Only expose store in development for debugging
if (config.features.debugLogging && typeof window !== 'undefined') {
  (window as unknown as { __REDUX_STORE__: typeof store }).__REDUX_STORE__ = store;
  // Also expose as __store for WebSocket middleware debugging
  (window as unknown as { __store: typeof store }).__store = store;
}
